/**
 * Hack to be able to send custom metrics.
 */

/**
 * Use this when not in the react context
 */
export const NewRelic = () => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return window?.newrelic;
};

/**
 * Use this in react components to send metrics.
 */
export const useNewRelic = () => {
  return {
    newRelic: NewRelic(),
  };
};

/**
 * Error type for when objects are thrown instead of proper Error instances
 */
export class ObjectError extends Error {
  public readonly attributes: { [key: string]: unknown };

  constructor(attributes: { [key: string]: unknown } = {}) {
    super('An object error was thrown');
    this.name = 'ObjectError';
    this.attributes = attributes;

    // Maintain proper stack trace for V8 engines
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ObjectError);
    }
  }
}

/**
 * Error type for when object errors cannot be parsed
 */
export class UnparsableObjectError extends Error {
  public readonly attributes: { [key: string]: unknown };

  constructor(attributes: { [key: string]: unknown } = {}) {
    super(
      'An object error was thrown, but was unable to be parsed by newRelicNoticeError',
    );
    this.name = 'UnparsableObjectError';
    this.attributes = attributes;

    // Maintain proper stack trace for V8 engines
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, UnparsableObjectError);
    }
  }
}

export function newRelicNoticeError(...args: unknown[]) {
  try {
    // Check if first argument is an Error object (including subclasses)
    if (args.length === 1 && args[0] instanceof Error) {
      NewRelic()?.noticeError(args[0] as Error);
      return;
    }

    // Original behavior for non-Error arguments
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: { [key: string]: any } = {};
    let argCounter = 1;

    // appends all arguments into a single object
    args.forEach((arg: unknown) => {
      if (typeof arg === 'string') {
        result[`arg${argCounter++}`] = arg;
      } else if (typeof arg === 'object' && !Array.isArray(arg)) {
        Object.assign(result, arg);
      }
    });

    // Create ObjectError with the same attributes as before
    const objectError = new ObjectError(result);
    NewRelic()?.noticeError(objectError);
  } catch (error) {
    // Create UnparsableObjectError when parsing fails
    const unparsableError = new UnparsableObjectError({
      message: args,
      newRelicNoticeError: 'Could not parse error object',
    });
    NewRelic()?.noticeError(unparsableError);
  }
}
